<template>
  <div ref="root" class="skills-marquee" aria-hidden="true">
    <div class="skills-marquee__inner">
      <div class="skills-marquee__column">
        <div ref="trackA" class="skills-marquee__track">
          <div ref="copyA" class="skills-marquee__copy">
            <div v-for="skill in columnA" :key="skill.key" class="skills-marquee__icon-box">
              <img :src="skill.icon" :alt="skill.name" class="skills-marquee__logo" decoding="async">
            </div>
          </div>
          <div class="skills-marquee__copy">
            <div v-for="skill in columnA" :key="`${skill.key}-dup`" class="skills-marquee__icon-box">
              <img :src="skill.icon" :alt="skill.name" class="skills-marquee__logo" decoding="async">
            </div>
          </div>
        </div>
      </div>

      <div v-if="columnB.length" class="skills-marquee__column">
        <div ref="trackB" class="skills-marquee__track">
          <div ref="copyB" class="skills-marquee__copy">
            <div v-for="skill in columnB" :key="skill.key" class="skills-marquee__icon-box">
              <img :src="skill.icon" :alt="skill.name" class="skills-marquee__logo" decoding="async">
            </div>
          </div>
          <div class="skills-marquee__copy">
            <div v-for="skill in columnB" :key="`${skill.key}-dup`" class="skills-marquee__icon-box">
              <img :src="skill.icon" :alt="skill.name" class="skills-marquee__logo" decoding="async">
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { skills } from '~/data/content';

// Purely decorative — the real, browsable Skills section lives on /explore
// now. Split into two columns for two independently-looping tracks.
const withKeys = skills.map((skill, index) => ({ ...skill, key: `${skill.name}-${index}` }));

// Where the hero stacks, this becomes one horizontal band at the seam between
// the photograph and the About column, and one band means one track carrying
// everything. Two counter-scrolling rows want height that a screen already
// split between a portrait and a page of prose does not have.
const horizontal = ref(false);

const columnA = computed(() =>
  horizontal.value ? withKeys : withKeys.filter((_, i) => i % 2 === 0)
);
const columnB = computed(() =>
  horizontal.value ? [] : withKeys.filter((_, i) => i % 2 !== 0)
);

const root = ref(null);
const trackA = ref(null);
const trackB = ref(null);
const copyA = ref(null);
const copyB = ref(null);

let resizeRaf = null;
let visibility = null;
let hiddenQuery = null;
let horizontalQuery = null;

function getGapPx(el, axis) {
  const gap = parseFloat(getComputedStyle(el)[axis]);
  return Number.isNaN(gap) ? 0 : gap;
}

// A genuinely seamless loop, measured once and then handed to CSS.
//
// The repeat distance is the thing that has to be right. The naive version
// animates translateY from 0 to -50% of a doubled track, which is off by half
// a gap: with N items and a gap G, one copy occupies N*(item+gap) but 50% of
// the doubled track is not that. Half a gap of drift per cycle is the visible
// snap. So one copy's real rendered height plus a single connecting gap is
// measured here and written to a custom property; the keyframes below travel
// exactly that far and the wrap is invisible.
//
// This used to be driven by hand off gsap.ticker, two callbacks writing a
// transform every frame for as long as the page was open. Measured on the
// deployed site, that was the single largest source of dropped frames: with
// the marquee removed the worst frame over 258 frames fell from 300ms to
// 16.8ms. A CSS animation of a transform runs on the compositor instead,
// costing no JavaScript per frame at all.
function measure() {
  // One row carries every skill, so it travels much further per cycle than a
  // column of half of them. Faster with it, or the band reads as stalled.
  const across = horizontal.value;
  const pairs = across
    ? [[trackA.value, copyA.value, 40]]
    : [
        [trackA.value, copyA.value, 18],
        [trackB.value, copyB.value, 15],
      ];

  for (const [track, copy, pxPerSecond] of pairs) {
    if (!track || !copy) continue;
    const distance = across
      ? copy.offsetWidth + getGapPx(track, 'columnGap')
      : copy.offsetHeight + getGapPx(track, 'rowGap');
    if (!distance) continue;
    track.style.setProperty('--loop-distance', `${distance}px`);
    track.style.setProperty('--loop-duration', `${distance / pxPerSecond}s`);
  }
}

function handleResize() {
  if (resizeRaf) cancelAnimationFrame(resizeRaf);
  resizeRaf = requestAnimationFrame(measure);
}

// Off screen, the animation is paused outright. A compositor animation is
// cheap but not free, and the hero panel is translated off to the left for
// most of the time anyone spends on this page.
function watchVisibility(root) {
  visibility = new IntersectionObserver(
    ([entry]) => {
      root.classList.toggle('is-running', entry.isIntersecting);
    },
    { threshold: 0 }
  );
  visibility.observe(root);
}

// Three regimes, and only the middle one draws nothing. Above 1100px the hero
// is two columns with room between them for a vertical marquee. Below 769px it
// is stacked and this is a horizontal band at the seam. In between the hero is
// still side by side but its gap has closed to a couple of rem, which is why
// that band alone stays hidden: there is nowhere for it to go.
const HORIZONTAL = '(max-width: 768px)';
const HIDDEN = '(min-width: 769px) and (max-width: 1100px)';

function sync() {
  horizontal.value = window.matchMedia(HORIZONTAL).matches;
  if (hiddenQuery?.matches) return;
  // After the class has been applied, or the row is measured while still laid
  // out as a column.
  requestAnimationFrame(measure);
}

onMounted(() => {
  hiddenQuery = window.matchMedia(HIDDEN);
  horizontalQuery = window.matchMedia(HORIZONTAL);

  horizontalQuery.addEventListener('change', sync);
  hiddenQuery.addEventListener('change', sync);
  sync();

  if (root.value) watchVisibility(root.value);
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  horizontalQuery?.removeEventListener('change', sync);
  hiddenQuery?.removeEventListener('change', sync);
  window.removeEventListener('resize', handleResize);
  if (resizeRaf) cancelAnimationFrame(resizeRaf);
  visibility?.disconnect();
});
</script>

<style lang="scss" scoped>
// A zero-width anchor in the hero's flex row. The marquee takes up no
// horizontal space at all, so changing how big it *looks* can never reflow
// the left/right hero columns — the hero owns the spacing between them via
// its own flex `gap`, and this sits exactly at the midpoint of that space
// (equal gaps on both sides of a zero-width item).
.skills-marquee {
  position: relative;
  flex: 0 0 0;
  width: 0;
  align-self: stretch;
  pointer-events: none;

  // Only here. Above this the hero has room between its columns for the
  // upright marquee; below it the hero stacks and this becomes a band at the
  // seam. In between the columns are still side by side with a couple of rem
  // between them, and there is nowhere for it to go.
  @media (min-width: 769px) and (max-width: 1100px) {
    display: none;
  }
}

// The visible marquee, centred on the anchor and taken out of flow so its
// size is purely cosmetic. Clipping + the fade mask live here, since this is
// the element that actually has a height.
.skills-marquee__inner {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  gap: clamp(0.5rem, 1vw, 1rem);
  height: clamp(320px, 62vh, 640px);
  overflow: hidden;
  opacity: 0.7;
  mask-image: linear-gradient(to bottom, transparent 0%, black 18%, black 82%, transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, transparent 0%, black 18%, black 82%, transparent 100%);
}

.skills-marquee__column {
  flex: 0 0 auto;
  overflow: hidden;
  display: flex;
  justify-content: center;
}

.skills-marquee__track {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(0.75rem, 2.5vh, 1.5rem);
  will-change: transform;
}

// The track is [copy1][copy2] stacked downward, so which direction is valid
// depends on where the duplicate sits:
//
//   up:   0 -> -distance. Scrolling up pulls copy2 in from below, and at
//         -distance copy2 sits exactly where copy1 began, so the wrap back to
//         0 is invisible.
//   down: -distance -> 0, never 0 -> +distance. Pushing down from 0 drags
//         empty space into view, because nothing is stacked above copy1.
.skills-marquee__column:first-child .skills-marquee__track {
  animation: marquee-up var(--loop-duration, 20s) linear infinite paused;
}

.skills-marquee__column:last-child .skills-marquee__track {
  animation: marquee-down var(--loop-duration, 20s) linear infinite paused;
}

@keyframes marquee-up {
  from { transform: translate3d(0, 0, 0); }
  to { transform: translate3d(0, calc(var(--loop-distance, 0px) * -1), 0); }
}

@keyframes marquee-down {
  from { transform: translate3d(0, calc(var(--loop-distance, 0px) * -1), 0); }
  to { transform: translate3d(0, 0, 0); }
}

// The row is [copy1][copy2] laid left to right, so the same reasoning as
// marquee-up applies on the other axis: pulling left brings copy2 in from the
// right, and at -distance it sits exactly where copy1 began.
@keyframes marquee-left {
  from { transform: translate3d(0, 0, 0); }
  to { transform: translate3d(calc(var(--loop-distance, 0px) * -1), 0, 0); }
}

// Paused is part of the shorthand above rather than a rule of its own, and this
// is why. A separate `.skills-marquee__track { animation-play-state: paused }`
// loses on specificity to `.skills-marquee__column:first-child
// .skills-marquee__track`, which carries the shorthand and resets play-state
// with it. Declaring it in the shorthand puts the two at the same weight, and
// only the class below, which is more specific still, turns it on.
//
// The home page is a horizontal track and this sits in the first of six panels,
// so it is off screen for most of a visit and should not be animating then.
.skills-marquee.is-running .skills-marquee__column:first-child .skills-marquee__track,
.skills-marquee.is-running .skills-marquee__column:last-child .skills-marquee__track {
  animation-play-state: running;
}

// Two columns of logos sliding forever is exactly the kind of motion this
// setting exists to stop.
@media (prefers-reduced-motion: reduce) {
  // !important, and it has to be. The animation is declared on
  // `.skills-marquee__column:first-child .skills-marquee__track`, three classes
  // deep, and the running state on a fourth. This selector is one class, so it
  // lost to both on specificity no matter where it sat in the sheet, and this
  // opt-out has never actually stopped anything.
  //
  // Matching them selector for selector would mean copying that whole chain
  // here and keeping the copy in step with it forever. A preference the reader
  // has stated outright is the one place a blunt override is the honest tool.
  .skills-marquee__track {
    animation: none !important;
    transform: none !important;
  }
}

.skills-marquee__copy {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(0.75rem, 2.5vh, 1.5rem);
}

// Each icon sits in its own soft container — no border, just a subtle fill,
// matching the fun-fact icon containers on the About column (same accent
// tint, same corner radius).
.skills-marquee__icon-box {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: clamp(42px, 4.2vw, 62px);
  height: clamp(42px, 4.2vw, 62px);
  aspect-ratio: 1;
  border-radius: 12px;
  background:
    // Layered over the page colour rather than left translucent. At 10% alpha
    // the HoneycombField backdrop reads straight through the tile, which looks
    // like the comb is painted on top of it. Renders identically, but opaque.
    linear-gradient(rgba(var(--accent-rgb), 0.1), rgba(var(--accent-rgb), 0.1)),
    var(--background);
}

.skills-marquee__logo {
  width: clamp(22px, 2.2vw, 32px);
  height: clamp(22px, 2.2vw, 32px);
  object-fit: contain;
  opacity: 0.45;
}

// Everything above is the upright marquee. This has to come after all of it:
// a media query carries no specificity of its own, so a base rule further down
// the sheet beats it on source order alone. That is exactly what happened the
// first time, and the copies stayed vertical stacks while the track slid
// sideways.
// Stacked hero, so the marquee lies down.
//
// Its place in the source order is already the seam between the photograph and
// the About column, which is the same thing it means on a desktop: the space
// between the two halves of the hero. Nothing moves in the markup.
@media (max-width: 768px) {
  .skills-marquee {
    // No longer a zero-width anchor. Here it is a band with a size of its own.
    //
    // And it keeps to the column it sits in rather than the screen. It did run
    // the full width, on the reasoning that a track stopping short reads as a
    // clipped box, and a mask was supposed to hide the ends. Masking the ends of
    // something that reaches the edge is not the same as it not reaching the
    // edge, and it showed.
    flex: 0 0 auto;
    width: 100%;
    // Slim, and subordinate to the portrait and the title it sits under. It is
    // inside the centred group now, so every pixel here is half a pixel of the
    // whole group moving up the screen.
    height: 2.75rem;
    align-self: auto;
  }

  .skills-marquee__inner {
    // Back in the flow: there is no anchor to be centred on any more.
    // relative, so the gradients below have something to sit in. It has to
    // cancel the offsets with it: the base rule is absolute at left/top 50%,
    // static made those inert, and relative does not. Left standing they push
    // the band half its own width right and half its height down.
    left: auto;
    top: auto;
    transform: none;
    width: 100%;
    justify-content: flex-start;
    // Centred in the band rather than padded away from its edges, so the band's
    // height is the one number that decides this.
    align-items: center;
    height: 100%;
    padding: 0;
    // The ends fade, and they do it with two painted gradients rather than a
    // mask.
    //
    // A mask is the obvious tool and it is the expensive one: masked content
    // has to be composited through an offscreen buffer, which is real work on
    // every paint of a strip that is also running an animation. The band sits
    // on the page's own background and nothing shows through it, so a gradient
    // from that colour to nothing is the same picture for a fraction of the
    // cost. It is also the difference between this element needing its own
    // buffer and not.
    //
    // The upright mask has to be turned off by name. Left standing it fades the
    // top and bottom of a 44px strip, and keeps the buffer this is here to
    // avoid.
    mask-image: none;
    -webkit-mask-image: none;
    position: relative;
  }

  .skills-marquee__inner::before,
  .skills-marquee__inner::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: 1;
    width: 14%;
    pointer-events: none;
  }

  .skills-marquee__inner::before {
    left: 0;
    background: linear-gradient(
      to right,
      var(--background),
      rgba(var(--background-rgb), 0)
    );
  }

  .skills-marquee__inner::after {
    right: 0;
    background: linear-gradient(
      to left,
      var(--background),
      rgba(var(--background-rgb), 0)
    );
  }

  .skills-marquee__column {
    width: 100%;
    justify-content: flex-start;
  }

  // Both, and to the same value: measure() adds one track gap to a copy's width
  // to find the loop distance, so an even rhythm across the seam needs the gap
  // between the copies to match the gap inside them.
  .skills-marquee__track,
  .skills-marquee__copy {
    flex-direction: row;
    gap: clamp(0.75rem, 3vw, 1.25rem);
  }

  // Only the name. The shorthand above carries `paused`, which the is-running
  // class is what turns on, and overriding the lot here would lose that.
  .skills-marquee__column:first-child .skills-marquee__track {
    animation-name: marquee-left;
  }

  .skills-marquee__icon-box {
    width: 30px;
    height: 30px;
    border-radius: 8px;
  }
}
</style>
